import React, { useEffect, useMemo, useState } from 'react';
import { Percent, AlertTriangle, CheckCircle2, XCircle, Info, Plus, X, Trash2 } from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import {
  fetchDynamicRules,
  saveDynamicRule,
  deactivateDynamicRule,
} from '../../services/dynamicCommissionService';
import {
  AgeBand,
  AmountBasis,
  AgeUnit,
  AMOUNT_BASES,
  DynamicCommissionRule,
  EvaluationResult,
  RecoveryEntryInput,
  commissionOn,
  evaluateRecoveryEntry,
  validateRule,
} from '../../utils/dynamicCommission/dynamicCommissionEngine';
import { formatINR } from '../../utils/watermark';

/**
 * COMMISSION RULE OPTION -- admin screen.
 *
 * Batch 101: rules list and recovery calculator.
 * Batch 103: Rule Builder (bank, zone, department, branches), Deactivate.
 * Batch 104: full builder: users, accounts, categories, product codes, amount range,
 *            age bands with a general-rate fallback.
 *
 * Saving writes ONLY to the new dynamicCommissionRules collection. No live payout changes.
 */

type Tab = 'rules' | 'calculator';

const BASIS_LABEL: Record<AmountBasis, string> = {
  collection: 'Collection Amount',
  outstanding: 'Outstanding Amount',
  principal: 'Principal Outstanding',
  totalRecoverable: 'Total Recoverable Amount',
};

const UNIT_LABEL: Record<AgeUnit, string> = { days: 'days', months: 'months', years: 'years' };

const STATUS_STYLE: Record<EvaluationResult['status'], { label: string; cls: string; Icon: React.FC<any> }> = {
  READY: { label: 'READY', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  BLOCKED: { label: 'BLOCKED', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: AlertTriangle },
  CONFLICT: { label: 'CONFLICT', cls: 'bg-red-50 text-red-700 border-red-200', Icon: XCircle },
  NO_MATCH: { label: 'NO MATCH', cls: 'bg-slate-100 text-slate-600 border-slate-200', Icon: Info },
};

/** Empty input means "not given" (null), never zero. */
const toAmount = (s: string): number | null => {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/** Comma or new-line separated list. Blank entries are dropped. */
const parseList = (s: string): string[] =>
  s
    .split(/[,\n]/)
    .map((x) => x.trim())
    .filter((x) => x.length > 0);

// Minimal shapes used for the cascading pickers.
interface ZoneLite { id: string; name: string; bankId?: string }
interface BranchLite { id: string; name: string; zoneId?: string; bankId?: string; departmentId?: string }
interface DeptLite { id: string; name: string; bankId?: string; zoneId?: string }
interface UserLite { id: string; name: string }

/** Branch belongs to the chosen zone, or else to the chosen bank (directly or through its zone). */
const branchInScope = (b: BranchLite, zones: ZoneLite[], bankId: string, zoneId: string): boolean => {
  if (zoneId) return b.zoneId === zoneId;
  if (!bankId) return true;
  if (b.bankId === bankId) return true;
  return !!b.zoneId && zones.some((z) => z.id === b.zoneId && z.bankId === bankId);
};

const branchOptionsFor = (
  branches: BranchLite[],
  zones: ZoneLite[],
  bankId: string,
  zoneId: string,
  departmentId: string,
): BranchLite[] =>
  branches.filter(
    (b) => branchInScope(b, zones, bankId, zoneId) && (!departmentId || b.departmentId === departmentId),
  );

const newRuleId = (): string =>
  `DCR-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;

export const DynamicCommissionSection: React.FC = () => {
  const { banks, zones, branches, users, recoveryDepartments, currentUser } = useSRMS();
  const [tab, setTab] = useState<Tab>('rules');
  const [rules, setRules] = useState<DynamicCommissionRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [listFilter, setListFilter] = useState<'active' | 'inactive' | 'all'>('active');
  const [editing, setEditing] = useState<DynamicCommissionRule | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // One read, only when this screen is opened.
  useEffect(() => {
    let cancelled = false;
    fetchDynamicRules()
      .then((list) => {
        if (!cancelled) setRules(list);
      })
      .catch((err: any) => {
        if (!cancelled) setLoadError(err?.message || 'Could not load the commission rules.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const bankName = (id?: string) => (id ? banks.find((b) => b.id === id)?.name ?? id : '');
  const zoneName = (id?: string) => (id ? zones.find((z) => z.id === id)?.name ?? id : '');
  const branchName = (id?: string) => (id ? branches.find((b) => b.id === id)?.name ?? id : '');
  const deptName = (id?: string) => (id ? recoveryDepartments.find((d) => d.id === id)?.name ?? id : '');
  const userName = (id?: string) => (id ? users.find((u) => u.id === id)?.name ?? id : '');

  const actor = currentUser ? { id: currentUser.id, name: currentUser.name } : null;

  const onSaved = (saved: DynamicCommissionRule, ms?: number) => {
    setRules((rs) => [saved, ...rs.filter((r) => r.id !== saved.id)]);
    setShowBuilder(false);
    setEditing(null);
    setActionError(null);
    setNotice(
      `Rule saved as version ${saved.version}: ${saved.name}. Live payouts follow it for new collections.` +
        (ms !== undefined ? ` (Saved in ${(ms / 1000).toFixed(1)} seconds.)` : ''),
    );
  };

  const deactivate = async (rule: DynamicCommissionRule) => {
    if (!actor) {
      setActionError('You must be signed in to change a rule.');
      return;
    }
    if (!window.confirm(`Deactivate "${rule.name}"? It will stop matching new entries. The rule is kept on record.`)) return;
    setBusyId(rule.id);
    setActionError(null);
    try {
      const updated = await deactivateDynamicRule(rule, actor);
      setRules((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
      setNotice(`Rule deactivated: ${rule.name}.`);
    } catch (err: any) {
      setActionError(err?.message || 'Could not deactivate the rule.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
          <Percent size={20} />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">Commission Rule Option</h2>
          <p className="text-sm text-slate-500">Dynamic commission rules and recovery calculator</p>
        </div>
      </div>

      <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
        This rule system is <b>live</b>. Saved rules apply to new collections and change agents' commission figures.
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        {(
          [
            { id: 'rules', label: `Rules (${rules.length})` },
            { id: 'calculator', label: 'Recovery Calculator' },
          ] as { id: Tab; label: string }[]
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
              tab === t.id ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'rules' && (
        <div className="space-y-3">
          {notice && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 flex items-start gap-2">
              <span className="flex-1">{notice}</span>
              <button type="button" onClick={() => setNotice(null)} className="text-emerald-700">
                <X size={14} />
              </button>
            </div>
          )}
          {actionError && <p className="text-sm text-red-600">{actionError}</p>}

          {!showBuilder && (
            <button
              type="button"
              onClick={() => {
                setNotice(null);
                setEditing(null);
                setShowBuilder(true);
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold"
            >
              <Plus size={16} /> New Rule
            </button>
          )}

          {showBuilder && (
            <RuleBuilder
              banks={banks.filter((b) => b.active)}
              zones={zones}
              branches={branches}
              departments={recoveryDepartments}
              users={users}
              actor={actor}
              initial={editing}
              onSaved={onSaved}
              onCancel={() => {
                setShowBuilder(false);
                setEditing(null);
              }}
            />
          )}

          {loading && <p className="text-sm text-slate-500">Loading rules…</p>}
          {loadError && <p className="text-sm text-red-600">{loadError}</p>}
          {!loading && !loadError && rules.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
              No dynamic rules yet. Press New Rule to create the first one.
            </div>
          )}
          {rules.length > 0 && (
            <div className="flex gap-2">
              {(
                [
                  { id: 'active', label: `Active (${rules.filter((r) => r.active).length})` },
                  { id: 'inactive', label: `Inactive (${rules.filter((r) => !r.active).length})` },
                  { id: 'all', label: `All (${rules.length})` },
                ] as { id: 'active' | 'inactive' | 'all'; label: string }[]
              ).map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setListFilter(f.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                    listFilter === f.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-300'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
          {rules
            .filter((r) => listFilter === 'all' || (listFilter === 'active' ? r.active : !r.active))
            .map((r) => (
              <RuleCard
                key={r.id}
                rule={r}
                bankName={bankName}
                zoneName={zoneName}
                branchName={branchName}
                deptName={deptName}
                userName={userName}
                busy={busyId === r.id}
                onDeactivate={() => deactivate(r)}
                onEdit={() => {
                  setNotice(null);
                  setEditing(r);
                  setShowBuilder(true);
                }}
              />
            ))}
        </div>
      )}

      {tab === 'calculator' && (
        <RecoveryCalculator
          banks={banks.filter((b) => b.active)}
          zones={zones}
          branches={branches}
          departments={recoveryDepartments}
          users={users}
          rules={rules}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------

const bandCondition = (b: AgeBand, unit: string): string =>
  b.operator === 'lt'
    ? `under ${b.value} ${unit}`
    : b.operator === 'gte'
      ? `${b.value} ${unit} or more`
      : `${b.from} to under ${b.to} ${unit}`;

const RuleCard: React.FC<{
  rule: DynamicCommissionRule;
  bankName: (id?: string) => string;
  zoneName: (id?: string) => string;
  branchName: (id?: string) => string;
  deptName: (id?: string) => string;
  userName: (id?: string) => string;
  busy: boolean;
  onDeactivate: () => void;
  onEdit: () => void;
}> = ({ rule, bankName, zoneName, branchName, deptName, userName, busy, onDeactivate, onEdit }) => {
  const scope: string[] = [];
  scope.push(`Bank: ${bankName(rule.bankId)}`);
  if (rule.zoneId) scope.push(`Zone: ${zoneName(rule.zoneId)}`);
  if (rule.departmentId) scope.push(`Department: ${deptName(rule.departmentId)}`);
  if (rule.branchId) scope.push(`Branch: ${branchName(rule.branchId)}`);
  if (rule.branchIds && rule.branchIds.length > 0) {
    scope.push(`Branches: ${rule.branchIds.map((id) => branchName(id)).join(', ')}`);
  }
  if (rule.userIds && rule.userIds.length > 0) {
    scope.push(`Users: ${rule.userIds.map((id) => userName(id)).join(', ')}`);
  }
  if (rule.accountIds && rule.accountIds.length > 0) scope.push(`Accounts: ${rule.accountIds.length}`);
  if (rule.categories && rule.categories.length > 0) scope.push(`Categories: ${rule.categories.join(', ')}`);
  if (rule.productCodes && rule.productCodes.length > 0) scope.push(`Product codes: ${rule.productCodes.join(', ')}`);
  if (rule.amountFrom !== undefined || rule.amountTo !== undefined) {
    const basis = rule.amountBasis ? BASIS_LABEL[rule.amountBasis] : 'amount';
    scope.push(`${basis} ${rule.amountFrom ?? 'any'} to ${rule.amountTo ?? 'any'}`);
  }

  const rates: string[] = [];
  if (rule.ageBands && rule.ageBands.length > 0) {
    const unit = rule.ageUnit ? UNIT_LABEL[rule.ageUnit] : '';
    rule.ageBands.forEach((b) => rates.push(`${bandCondition(b, unit)}: ${b.rate}%`));
  }
  if (rule.generalRate !== undefined) {
    rates.push(rule.ageBands && rule.ageBands.length > 0 ? `no band matches: ${rule.generalRate}%` : `general: ${rule.generalRate}%`);
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-slate-900">{rule.name}</span>
        <span className="text-xs text-slate-400">{rule.id} · v{rule.version}</span>
        <span
          className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full border ${
            rule.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'
          }`}
        >
          {rule.active ? 'Active' : 'Inactive'}
        </span>
      </div>
      <p className="mt-2 text-sm text-slate-600">{scope.join(' · ')}</p>
      <p className="mt-1 text-sm text-slate-800">Rate: {rates.length > 0 ? rates.join(' · ') : 'none'}</p>
      <p className="mt-1 text-xs text-slate-500">
        Effective {rule.effectiveFrom}
        {rule.effectiveTo ? ` to ${rule.effectiveTo}` : ' onward'} · Commission on {BASIS_LABEL[rule.commissionBase]}
      </p>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={onEdit}
          disabled={busy}
          className="rounded-lg border border-slate-300 text-slate-800 px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
        >
          Update rule
        </button>
        {rule.active && (
          <button
            type="button"
            onClick={onDeactivate}
            disabled={busy}
            className="rounded-lg border border-red-300 text-red-700 px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
          >
            {busy ? 'Deactivating…' : 'Deactivate'}
          </button>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

interface BandRow {
  key: number;
  op: 'under' | 'range' | 'orMore';
  value: string;
  from: string;
  to: string;
  rate: string;
}

let bandSeq = 1;
const newBandRow = (): BandRow => ({ key: bandSeq++, op: 'under', value: '', from: '', to: '', rate: '' });

/** Converts a form row to an engine age band. Missing numbers become NaN, which validation rejects. */
const toAgeBand = (r: BandRow, index: number): AgeBand => {
  const id = `B${index + 1}`;
  const rate = toAmount(r.rate) ?? NaN;
  if (r.op === 'under') return { id, operator: 'lt', value: toAmount(r.value) ?? NaN, rate };
  if (r.op === 'orMore') return { id, operator: 'gte', value: toAmount(r.value) ?? NaN, rate };
  return { id, operator: 'range', from: toAmount(r.from) ?? NaN, to: toAmount(r.to) ?? NaN, rate };
};

const rowCondition = (r: BandRow, unit: string): string => {
  if (r.op === 'under') return `under ${r.value || '?'} ${unit}`;
  if (r.op === 'orMore') return `${r.value || '?'} ${unit} or more`;
  return `${r.from || '?'} to under ${r.to || '?'} ${unit}`;
};

const RuleBuilder: React.FC<{
  banks: { id: string; name: string }[];
  zones: ZoneLite[];
  branches: BranchLite[];
  departments: DeptLite[];
  users: UserLite[];
  actor: { id: string; name: string } | null;
  /** When set, the builder opens filled in with this rule and saving creates its next version. */
  initial?: DynamicCommissionRule | null;
  onSaved: (rule: DynamicCommissionRule, ms?: number) => void;
  onCancel: () => void;
}> = ({ banks, zones, branches, departments, users, actor, initial, onSaved, onCancel }) => {
  const init = initial ?? null;
  const [ruleId] = useState<string>(() => init?.id ?? newRuleId());
  const [name, setName] = useState(init?.name ?? '');
  const [bankId, setBankId] = useState(init?.bankId ?? '');
  const [zoneId, setZoneId] = useState(init?.zoneId ?? '');
  const [departmentId, setDepartmentId] = useState(init?.departmentId ?? '');
  const [branchIds, setBranchIds] = useState<string[]>(init?.branchIds ?? []);
  const [userIds, setUserIds] = useState<string[]>(init?.userIds ?? []);
  const [accountText, setAccountText] = useState((init?.accountIds ?? []).join('\n'));
  const [categoryText, setCategoryText] = useState((init?.categories ?? []).join(', '));
  const [productText, setProductText] = useState((init?.productCodes ?? []).join(', '));
  const [amountBasis, setAmountBasis] = useState<AmountBasis>(init?.amountBasis ?? 'collection');
  const [amountFromText, setAmountFromText] = useState(init?.amountFrom !== undefined ? String(init.amountFrom) : '');
  const [amountToText, setAmountToText] = useState(init?.amountTo !== undefined ? String(init.amountTo) : '');
  const [ageUnit, setAgeUnit] = useState<AgeUnit>(init?.ageUnit ?? 'months');
  const [bandRows, setBandRows] = useState<BandRow[]>(() =>
    (init?.ageBands ?? []).map((b) => ({
      key: bandSeq++,
      op: (b.operator === 'lt' ? 'under' : b.operator === 'gte' ? 'orMore' : 'range') as BandRow['op'],
      value: b.operator === 'range' ? '' : String(b.value ?? ''),
      from: b.operator === 'range' ? String(b.from ?? '') : '',
      to: b.operator === 'range' ? String(b.to ?? '') : '',
      rate: String(b.rate),
    })),
  );
  const [generalRateText, setGeneralRateText] = useState(
    init?.generalRate !== undefined ? String(init.generalRate) : '',
  );
  const [base, setBase] = useState<AmountBasis>(init?.commissionBase ?? 'collection');
  const [effectiveFrom, setEffectiveFrom] = useState(init?.effectiveFrom ?? '');
  const [effectiveTo, setEffectiveTo] = useState(init?.effectiveTo ?? '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Cascade: a parent change clears every child that no longer belongs to it.
  const pickBank = (v: string) => {
    setBankId(v);
    setZoneId('');
    setDepartmentId('');
    setBranchIds([]);
  };
  const pickZone = (v: string) => {
    setZoneId(v);
    setDepartmentId('');
    setBranchIds([]);
  };
  const pickDepartment = (v: string) => {
    setDepartmentId(v);
    setBranchIds([]);
  };

  const zoneOptions = useMemo(() => zones.filter((z) => !bankId || z.bankId === bankId), [zones, bankId]);
  const deptOptions = useMemo(
    () => departments.filter((d) => (!bankId || d.bankId === bankId) && (!zoneId || d.zoneId === zoneId)),
    [departments, bankId, zoneId],
  );
  const branchOptions = useMemo(
    () => branchOptionsFor(branches, zones, bankId, zoneId, departmentId),
    [branches, zones, bankId, zoneId, departmentId],
  );
  // Hierarchy: agents shown are only those of the chosen bank who cover one of the
  // branches shown above (or belong to the chosen department).
  const scopedUsers = useMemo(() => {
    if (!bankId) return users;
    const shown = new Set(branchOptions.map((b) => b.id));
    return users.filter((u) => {
      if (u.bankId && u.bankId !== bankId) return false;
      const covered = [...(u.branchIds ?? []), ...(u.branchId ? [u.branchId] : [])];
      const coversShown = covered.some((id) => shown.has(id));
      const inDept = !!departmentId && u.departmentId === departmentId;
      return coversShown || inDept;
    });
  }, [users, bankId, departmentId, branchOptions]);

  const toggle = (list: string[], set: (v: string[]) => void, id: string) =>
    set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const updateBand = (key: number, patch: Partial<BandRow>) =>
    setBandRows((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeBand = (key: number) => setBandRows((rows) => rows.filter((r) => r.key !== key));

  // Build the engine rule from the form. Empty optional fields are left out, never zero.
  const ageBands = bandRows.map((r, i) => toAgeBand(r, i));
  const amountFrom = toAmount(amountFromText) ?? undefined;
  const amountTo = toAmount(amountToText) ?? undefined;
  const generalRate = toAmount(generalRateText) ?? undefined;
  const accounts = parseList(accountText);
  const categories = parseList(categoryText);
  const productCodes = parseList(productText);
  const hasAmount = amountFrom !== undefined || amountTo !== undefined;
  const hasBands = ageBands.length > 0;

  const draft: DynamicCommissionRule = {
    id: ruleId,
    name: name.trim(),
    version: 1,
    active: true,
    bankId,
    zoneId: zoneId || undefined,
    departmentId: departmentId || undefined,
    branchIds: branchIds.length > 0 ? branchIds : undefined,
    userIds: userIds.length > 0 ? userIds : undefined,
    accountIds: accounts.length > 0 ? accounts : undefined,
    categories: categories.length > 0 ? categories : undefined,
    productCodes: productCodes.length > 0 ? productCodes : undefined,
    amountBasis: hasAmount ? amountBasis : undefined,
    amountFrom,
    amountTo,
    ageUnit: hasBands ? ageUnit : undefined,
    ageBands: hasBands ? ageBands : undefined,
    generalRate,
    commissionBase: base,
    effectiveFrom,
    effectiveTo: effectiveTo || undefined,
  };
  const errors = validateRule(draft);

  const save = async () => {
    if (!actor) {
      setSaveError('You must be signed in to save a rule.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const t0 = Date.now();
      const saved = await saveDynamicRule(draft, init, actor);
      const ms = Date.now() - t0;
      console.info(`[rule save] total ${ms} ms`);
      onSaved(saved, ms);
    } catch (err: any) {
      setSaveError(err?.message || 'Could not save the rule.');
    } finally {
      setSaving(false);
    }
  };

  const input = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white';
  const label = 'block text-xs font-semibold text-slate-600 mb-1';
  const unitWord = UNIT_LABEL[ageUnit];
  const bankLabel = banks.find((b) => b.id === bankId)?.name ?? '';
  const zoneLabel = zones.find((z) => z.id === zoneId)?.name ?? 'any zone';
  const deptLabel = departments.find((d) => d.id === departmentId)?.name ?? 'any department';

  const ex = 10000;
  const formulaLines: string[] = [];
  ageBands.forEach((b, i) => {
    if (Number.isFinite(b.rate)) {
      const c = commissionOn(ex, b.rate);
      formulaLines.push(
        `${rowCondition(bandRows[i], unitWord)}: ${formatINR(ex)} × ${b.rate}% = ${c === null ? '—' : formatINR(c)}`,
      );
    }
  });
  if (generalRate !== undefined) {
    const c = commissionOn(ex, generalRate);
    formulaLines.push(
      `${hasBands ? 'No band matches' : 'Every entry'}: ${formatINR(ex)} × ${generalRate}% = ${c === null ? '—' : formatINR(c)}`,
    );
  }

  if (hasBands && generalRate === undefined) {
    formulaLines.push('Note: no general rate is set. Entries outside every band get no commission.');
  }

  const applies: string[] = [
    `bank is ${bankLabel || '—'}`,
    `zone is ${zoneLabel}`,
    `department is ${deptLabel}`,
    branchIds.length > 0 ? `branch is one of ${branchIds.length} ticked` : 'branch is any in that selection',
  ];
  if (userIds.length > 0) applies.push(`agent is one of ${userIds.length} ticked`);
  if (accounts.length > 0) applies.push(`account is one of ${accounts.length} listed`);
  if (categories.length > 0) applies.push(`category is ${categories.join(' or ')}`);
  if (productCodes.length > 0) applies.push(`product code is ${productCodes.join(' or ')}`);
  if (hasAmount) {
    applies.push(
      `${BASIS_LABEL[amountBasis]} from ${amountFrom ?? 'any'} to ${amountTo ?? 'any'}`,
    );
  }
  if (hasBands) applies.push(`account age (in ${unitWord}) falls in one of the bands`);
  applies.push(`collection date from ${effectiveFrom || '—'} to ${effectiveTo || 'no end date'}`);

  const sectionTitle = 'text-xs font-bold uppercase tracking-wide text-slate-500 pt-2';

  return (
    <div className="rounded-xl border-2 border-slate-900 bg-white p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-slate-900">{init ? `Update commission rule (saves as v${init.version + 1})` : 'New commission rule'}</h3>
        <button type="button" onClick={onCancel} className="text-slate-500 hover:text-slate-800 text-sm">Cancel</button>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className={label}>Rule name</label>
          <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. BOI KOLHAPUR ZO COMM RULE" />
        </div>

        <div>
          <label className={label}>1. Bank</label>
          <select className={input} value={bankId} onChange={(e) => pickBank(e.target.value)}>
            <option value="">Select bank</option>
            {banks.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={label}>2. Zone (optional)</label>
          <select className={input} value={zoneId} onChange={(e) => pickZone(e.target.value)} disabled={!bankId}>
            <option value="">All zones of this bank</option>
            {zoneOptions.map((z) => (
              <option key={z.id} value={z.id}>{z.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={label}>3. Recovery department (optional)</label>
          <select className={input} value={departmentId} onChange={(e) => pickDepartment(e.target.value)} disabled={!bankId}>
            <option value="">Any department</option>
            {deptOptions.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>
        <div />

        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-1">
            <label className={label}>4. Branches (tick the ones this rule covers)</label>
            <div className="flex gap-3 text-xs font-semibold">
              <button
                type="button"
                className="text-slate-700 underline disabled:opacity-40"
                disabled={branchOptions.length === 0}
                onClick={() => setBranchIds(branchOptions.map((b) => b.id))}
              >
                Tick all shown
              </button>
              <button
                type="button"
                className="text-slate-700 underline disabled:opacity-40"
                disabled={branchIds.length === 0}
                onClick={() => setBranchIds([])}
              >
                Clear
              </button>
            </div>
          </div>
          {!bankId ? (
            <p className="text-sm text-slate-500 rounded-lg border border-dashed border-slate-300 p-3">Select a bank first.</p>
          ) : branchOptions.length === 0 ? (
            <p className="text-sm text-slate-500 rounded-lg border border-dashed border-slate-300 p-3">
              No branches found for this selection. Check that branches are linked to this zone or department.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-56 overflow-y-auto rounded-lg border border-slate-200 p-2">
              {branchOptions.map((b) => (
                <label key={b.id} className="flex items-center gap-2 text-sm text-slate-800 px-2 py-1 rounded hover:bg-slate-50">
                  <input type="checkbox" checked={branchIds.includes(b.id)} onChange={() => toggle(branchIds, setBranchIds, b.id)} />
                  {b.name}
                </label>
              ))}
            </div>
          )}
          <p className="mt-1 text-xs text-slate-500">
            {branchIds.length === 0
              ? 'No branch ticked: the rule covers every branch in the selection above.'
              : `${branchIds.length} branch${branchIds.length === 1 ? '' : 'es'} ticked.`}
          </p>
        </div>

        <div className="md:col-span-2">
          <label className={label}>5. Agents (tick the ones this rule covers; none = all agents)</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-48 overflow-y-auto rounded-lg border border-slate-200 p-2">
            {scopedUsers.map((u) => (
              <label key={u.id} className="flex items-center gap-2 text-sm text-slate-800 px-2 py-1 rounded hover:bg-slate-50">
                <input type="checkbox" checked={userIds.includes(u.id)} onChange={() => toggle(userIds, setUserIds, u.id)} />
                {u.name}
              </label>
            ))}
          </div>
        </div>

        <div className="md:col-span-2">
          <label className={label}>6. Account IDs (optional; separate with commas or new lines)</label>
          <textarea className={input} rows={2} value={accountText} onChange={(e) => setAccountText(e.target.value)} placeholder="Leave empty for all accounts" />
        </div>

        <div>
          <label className={label}>7. Account categories (optional; comma separated)</label>
          <input className={input} value={categoryText} onChange={(e) => setCategoryText(e.target.value)} placeholder="e.g. A, B" />
        </div>
        <div>
          <label className={label}>8. Product codes (optional; comma separated)</label>
          <input className={input} value={productText} onChange={(e) => setProductText(e.target.value)} placeholder="e.g. PL01, HL02" />
        </div>

        <div className="md:col-span-2 grid gap-3 md:grid-cols-3">
          <div>
            <label className={label}>9. Amount basis (optional)</label>
            <select className={input} value={amountBasis} onChange={(e) => setAmountBasis(e.target.value as AmountBasis)}>
              {AMOUNT_BASES.map((b) => (
                <option key={b} value={b}>{BASIS_LABEL[b]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={label}>Amount from (₹)</label>
            <input inputMode="decimal" className={input} value={amountFromText} onChange={(e) => setAmountFromText(e.target.value)} placeholder="blank = no minimum" />
          </div>
          <div>
            <label className={label}>Amount to (₹)</label>
            <input inputMode="decimal" className={input} value={amountToText} onChange={(e) => setAmountToText(e.target.value)} placeholder="blank = no maximum" />
          </div>
        </div>

        <div className="md:col-span-2 space-y-2">
          <div className="flex items-center justify-between">
            <label className={label}>10. Age bands (optional)</label>
            <div className="flex items-center gap-2">
              <select className="rounded-lg border border-slate-300 px-2 py-1 text-xs" value={ageUnit} onChange={(e) => setAgeUnit(e.target.value as AgeUnit)}>
                <option value="days">days</option>
                <option value="months">months</option>
                <option value="years">years</option>
              </select>
              <button
                type="button"
                onClick={() => setBandRows((rows) => [...rows, newBandRow()])}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold"
              >
                <Plus size={12} /> Add band
              </button>
            </div>
          </div>
          {bandRows.length === 0 && (
            <p className="text-sm text-slate-500 rounded-lg border border-dashed border-slate-300 p-3">
              No age bands: the general rate below applies to every entry.
            </p>
          )}
          {bandRows.map((r) => (
            <div key={r.key} className="grid grid-cols-12 gap-2 items-center rounded-lg border border-slate-200 p-2">
              <select className="col-span-3 rounded-lg border border-slate-300 px-2 py-2 text-sm" value={r.op} onChange={(e) => updateBand(r.key, { op: e.target.value as BandRow['op'] })}>
                <option value="under">under</option>
                <option value="range">from … to under</option>
                <option value="orMore">or more</option>
              </select>
              {r.op === 'range' ? (
                <>
                  <input inputMode="decimal" className="col-span-2 rounded-lg border border-slate-300 px-2 py-2 text-sm" placeholder="from" value={r.from} onChange={(e) => updateBand(r.key, { from: e.target.value })} />
                  <input inputMode="decimal" className="col-span-2 rounded-lg border border-slate-300 px-2 py-2 text-sm" placeholder="to" value={r.to} onChange={(e) => updateBand(r.key, { to: e.target.value })} />
                </>
              ) : (
                <input inputMode="decimal" className="col-span-4 rounded-lg border border-slate-300 px-2 py-2 text-sm" placeholder={unitWord} value={r.value} onChange={(e) => updateBand(r.key, { value: e.target.value })} />
              )}
              <input inputMode="decimal" className="col-span-2 rounded-lg border border-slate-300 px-2 py-2 text-sm" placeholder="rate %" value={r.rate} onChange={(e) => updateBand(r.key, { rate: e.target.value })} />
              <span className="col-span-1 text-xs text-slate-500">{unitWord}</span>
              <button type="button" onClick={() => removeBand(r.key)} className="text-red-600" aria-label="Remove band">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <p className="text-xs text-slate-500">
            Account age runs from the NPA date to the collection date. "From" is included, "to" is not.
          </p>
        </div>

        <div>
          <label className={label}>General rate (%) {hasBands ? '— used when no band matches' : '— required if no bands'}</label>
          <input inputMode="decimal" className={input} value={generalRateText} onChange={(e) => setGeneralRateText(e.target.value)} placeholder="e.g. 2.5" />
        </div>
        <div>
          <label className={label}>11. Commission is calculated on</label>
          <select className={input} value={base} onChange={(e) => setBase(e.target.value as AmountBasis)}>
            {AMOUNT_BASES.map((b) => (
              <option key={b} value={b}>{BASIS_LABEL[b]}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={label}>Effective from</label>
          <input type="date" className={input} value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
        </div>
        <div>
          <label className={label}>Effective to (optional)</label>
          <input type="date" className={input} value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)} />
        </div>
      </div>

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm space-y-1">
        <p className="font-semibold text-slate-900">Formula preview</p>
        <p>Commission = {BASIS_LABEL[base]} × rate ÷ 100</p>
        {formulaLines.map((l, i) => (
          <p key={i}>{l}</p>
        ))}
        <p className="text-slate-600">Applies when: {applies.join('; ')}.</p>
      </div>

      {errors.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <p className="font-semibold mb-1">Still needed before saving:</p>
          <ul className="list-disc pl-5 space-y-0.5">
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}
      {saveError && <p className="text-sm text-red-600">{saveError}</p>}

      <button
        type="button"
        onClick={save}
        disabled={saving || errors.length > 0}
        className="w-full rounded-lg bg-slate-900 text-white py-2.5 text-sm font-semibold disabled:opacity-40"
      >
        {saving ? 'Saving…' : 'Save rule'}
      </button>
      <p className="text-xs text-slate-500">Saving applies the rule to new collections straight away.</p>
    </div>
  );
};

// ---------------------------------------------------------------------------

interface CalcForm {
  bankId: string;
  zoneId: string;
  departmentId: string;
  branchId: string;
  userId: string;
  accountId: string;
  category: string;
  productCode: string;
  collectionDate: string;
  npaDate: string;
  collection: string;
  outstanding: string;
  principal: string;
  totalRecoverable: string;
}

const EMPTY_FORM: CalcForm = {
  bankId: '',
  zoneId: '',
  departmentId: '',
  branchId: '',
  userId: '',
  accountId: '',
  category: '',
  productCode: '',
  collectionDate: '',
  npaDate: '',
  collection: '',
  outstanding: '',
  principal: '',
  totalRecoverable: '',
};

const RecoveryCalculator: React.FC<{
  banks: { id: string; name: string }[];
  zones: ZoneLite[];
  branches: BranchLite[];
  departments: DeptLite[];
  users: UserLite[];
  rules: DynamicCommissionRule[];
}> = ({ banks, zones, branches, departments, users, rules }) => {
  const [form, setForm] = useState<CalcForm>(EMPTY_FORM);
  const [result, setResult] = useState<EvaluationResult | null>(null);

  const set = (key: keyof CalcForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.value;
    setForm((f) => {
      const next = { ...f, [key]: value } as CalcForm;
      // Changing a parent clears the children that no longer belong to it.
      if (key === 'bankId') {
        next.zoneId = '';
        next.departmentId = '';
        next.branchId = '';
      }
      if (key === 'zoneId') {
        next.departmentId = '';
        next.branchId = '';
      }
      if (key === 'departmentId') next.branchId = '';
      return next;
    });
    setResult(null);
  };

  const zoneOptions = useMemo(
    () => zones.filter((z) => !form.bankId || z.bankId === form.bankId),
    [zones, form.bankId],
  );
  const deptOptions = useMemo(
    () =>
      departments.filter(
        (d) => (!form.bankId || d.bankId === form.bankId) && (!form.zoneId || d.zoneId === form.zoneId),
      ),
    [departments, form.bankId, form.zoneId],
  );
  const branchOptions = useMemo(
    () => branchOptionsFor(branches, zones, form.bankId, form.zoneId, form.departmentId),
    [branches, zones, form.bankId, form.zoneId, form.departmentId],
  );

  const calculate = () => {
    const entry: RecoveryEntryInput = {
      id: 'CALC',
      accountId: form.accountId.trim(),
      bankId: form.bankId,
      zoneId: form.zoneId || undefined,
      departmentId: form.departmentId || null,
      branchId: form.branchId || undefined,
      userId: form.userId,
      collectionDate: form.collectionDate,
      npaDate: form.npaDate || null,
      category: form.category.trim() || null,
      productCode: form.productCode.trim() || null,
      amounts: {
        collection: toAmount(form.collection),
        outstanding: toAmount(form.outstanding),
        principal: toAmount(form.principal),
        totalRecoverable: toAmount(form.totalRecoverable),
      },
    };
    setResult(evaluateRecoveryEntry(entry, rules));
  };

  const input = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm';
  const label = 'block text-xs font-semibold text-slate-600 mb-1';

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <p className="text-sm text-slate-500">Enter one recovery. Nothing is saved.</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={label}>Bank</label>
            <select className={input} value={form.bankId} onChange={set('bankId')}>
              <option value="">Select bank</option>
              {banks.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={label}>Zone</label>
            <select className={input} value={form.zoneId} onChange={set('zoneId')}>
              <option value="">Any zone</option>
              {zoneOptions.map((z) => (
                <option key={z.id} value={z.id}>{z.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={label}>Recovery department</label>
            <select className={input} value={form.departmentId} onChange={set('departmentId')}>
              <option value="">Any department</option>
              {deptOptions.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={label}>Branch</label>
            <select className={input} value={form.branchId} onChange={set('branchId')}>
              <option value="">Any branch</option>
              {branchOptions.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={label}>Agent / user</label>
            <select className={input} value={form.userId} onChange={set('userId')}>
              <option value="">Select user</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={label}>Account ID</label>
            <input className={input} value={form.accountId} onChange={set('accountId')} />
          </div>
          <div>
            <label className={label}>Account category</label>
            <input className={input} value={form.category} onChange={set('category')} />
          </div>
          <div>
            <label className={label}>Product code</label>
            <input className={input} value={form.productCode} onChange={set('productCode')} />
          </div>
          <div>
            <label className={label}>NPA date</label>
            <input type="date" className={input} value={form.npaDate} onChange={set('npaDate')} />
          </div>
          <div>
            <label className={label}>Collection date</label>
            <input type="date" className={input} value={form.collectionDate} onChange={set('collectionDate')} />
          </div>
          <div>
            <label className={label}>Collection amount (₹)</label>
            <input inputMode="decimal" className={input} value={form.collection} onChange={set('collection')} />
          </div>
          <div>
            <label className={label}>Outstanding amount (₹)</label>
            <input inputMode="decimal" className={input} value={form.outstanding} onChange={set('outstanding')} />
          </div>
          <div>
            <label className={label}>Principal outstanding (₹)</label>
            <input inputMode="decimal" className={input} value={form.principal} onChange={set('principal')} />
          </div>
          <div>
            <label className={label}>Total recoverable (₹)</label>
            <input inputMode="decimal" className={input} value={form.totalRecoverable} onChange={set('totalRecoverable')} />
          </div>
        </div>
        <button
          type="button"
          onClick={calculate}
          disabled={!form.bankId || !form.userId || !form.collectionDate}
          className="w-full rounded-lg bg-slate-900 text-white py-2.5 text-sm font-semibold disabled:opacity-40"
        >
          Calculate
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        {!result && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Info size={16} /> Fill in the entry and press Calculate.
          </div>
        )}
        {result && <ResultView result={result} />}
      </div>
    </div>
  );
};

const ResultView: React.FC<{ result: EvaluationResult }> = ({ result }) => {
  const st = STATUS_STYLE[result.status];
  const Icon = st.Icon;
  return (
    <div className="space-y-3">
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-sm font-semibold ${st.cls}`}>
        <Icon size={16} /> {st.label}
      </span>
      {result.status === 'READY' && result.commission !== undefined && (
        <div className="text-sm text-slate-800 space-y-1">
          <p>Rule: <b>{result.ruleIds.join(', ')}</b></p>
          <p>Commission base: {formatINR(result.base ?? 0)}</p>
          <p>Rate: {result.rate}%</p>
          <p className="text-base font-bold">Commission: {formatINR(result.commission)}</p>
        </div>
      )}
      {result.ruleIds.length > 0 && result.status !== 'READY' && (
        <p className="text-sm text-slate-700">Rules involved: {result.ruleIds.join(', ')}</p>
      )}
      {result.messages.length > 0 && (
        <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
          {result.messages.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      )}
    </div>
  );
};
